import { useEffect, useRef } from 'react';
import { Gradient } from '@/lib/Gradient';

interface StripeMeshGradientProps {
  color1?: string;
  color2?: string;
  color3?: string;
  color4?: string;
  className?: string;
}

/**
 * Stripe-style animated WebGL mesh gradient background.
 * ~10kb, GPU-accelerated, minimal CPU overhead.
 */
export function StripeMeshGradient({
  color1 = '#1a0000',
  color2 = '#1e2239',
  color3 = '#0a0a0a',
  color4 = '#2a0a0a',
  className = '',
}: StripeMeshGradientProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gradientRef = useRef<any>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    try {
      // @ts-ignore
      const gradient = new Gradient();
      gradientRef.current = gradient;
      gradient.initGradient('#stripe-mesh-canvas');
      console.log('[StripeMeshGradient] Initialized successfully');
    } catch (err) {
      console.warn('[StripeMeshGradient] WebGL init failed:', err);
    }

    return () => {
      if (gradientRef.current) {
        try {
          gradientRef.current.disconnect();
        } catch (_) {}
        gradientRef.current = null;
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="stripe-mesh-canvas"
      className={`absolute inset-0 w-full h-full ${className}`}
      style={{
        '--gradient-color-1': color1,
        '--gradient-color-2': color2,
        '--gradient-color-3': color3,
        '--gradient-color-4': color4,
      } as React.CSSProperties}
      aria-hidden="true"
    />
  );
}
